import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { PhoneIncoming, Check, Mail } from "lucide-react"
import { toast } from "@/components/portal/Toast"

/**
 * #86 (Robin Sand, 09.09.2026): Diese Mail ist die Rückmeldung zu einem noch
 * offenen Telefonat desselben Kontakts. Wer die Mail bearbeitet, ist oft nicht
 * der, der telefoniert hat — ohne diesen Hinweis bleibt der Anruf ewig „offen".
 *
 * Der Haken ruft denselben Endpunkt wie die Nacharbeit-Zelle in der
 * KI-Telefon-Liste (Voice #4) — eine Quelle für „erledigt", nicht zwei.
 */
export type OffenerAnruf = {
  call_id: string
  /** `bot` = KI Telefon (Voice-Agent), `sipgate` = Mitarbeiter am Telefon */
  quelle?: "bot" | "sipgate" | string
  kanal?: string | null
  started_at?: string | null
  ai_headline?: string | null
  caller_number?: string | null
  zugewiesen_an?: string | null
}

/** #86: was am Kontakt nach dem Anruf passiert ist — Übergabe intern */
export type FolgeAktivitaet = {
  art: "kundenmail" | "mail_bearbeitet" | "anruf_sipgate" | "anruf_bot" | string
  zeit?: string | null
  wer?: string | null
  text?: string | null
  email_id?: number | null
  call_id?: string | null
  caller_number?: string | null
}

export type RueckmeldungMail = {
  email_id: number
  subject?: string | null
  received_at?: string | null
  sender?: string | null
}

export function fmtTs(iso?: string | null): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  } catch { return iso }
}

/** #86: Chip „Rückmeldung per Mail" am Anruf — führt zur Mail, nicht zur ID.
 *  Gemeinsam für KI Telefon und Sipgate, damit es einmal aussieht. */
export function RueckmeldungMailChip({ mail }: { mail: RueckmeldungMail }) {
  return (
    <Link
      to={`/emails/${mail.email_id}`}
      onClick={(e) => e.stopPropagation()}
      title={mail.subject || undefined}
      className="inline-flex items-center gap-1 mt-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 hover:underline"
      data-testid="rueckmeldung-mail-chip"
    >
      <Mail className="w-3 h-3" /> Rückmeldung per Mail {fmtTs(mail.received_at)}
    </Link>
  )
}

function folgeLabel(a: FolgeAktivitaet): string {
  const zeit = fmtTs(a.zeit)
  switch (a.art) {
    case "kundenmail": return `Rückmeldung per Mail ${zeit}`
    case "mail_bearbeitet": return a.wer ? `${a.wer} per Mail ${zeit}` : `Per Mail bearbeitet ${zeit}`
    case "anruf_sipgate": return a.wer ? `Erneut telefoniert ${zeit} (${a.wer})` : `Erneut telefoniert ${zeit}`
    case "anruf_bot": return `Erneut angerufen ${zeit} (KI Telefon)`
    default: return `${a.art} ${zeit}`
  }
}

function folgeZiel(a: FolgeAktivitaet): string {
  if (a.email_id) return `/emails/${a.email_id}`
  if (a.art === "anruf_sipgate" && a.call_id) return `/sipgate/${encodeURIComponent(a.call_id)}`
  if (a.caller_number) return `/ki-telefon?caller=${encodeURIComponent(a.caller_number)}`
  return "/ki-telefon"
}

/** #86: Chips „wer war danach dran" am nicht abgehakten Anruf — Kundenmail
 *  gelb, Bearbeitung durch Kollegen blau, weiterer Anruf grau. */
export function FolgeAktivitaetChips({ liste }: { liste: FolgeAktivitaet[] | null | undefined }) {
  if (!liste || liste.length === 0) return null
  return (
    <span className="flex flex-wrap gap-1 mt-1" data-testid="folge-aktivitaeten">
      {liste.map((a, i) => (
        <Link
          key={i}
          to={folgeZiel(a)}
          onClick={(e) => e.stopPropagation()}
          title={a.text || undefined}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium hover:underline ${
            a.art === "kundenmail"
              ? "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
              : a.art === "mail_bearbeitet"
                ? "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200"
                : "bg-secondary text-foreground"
          }`}
        >
          {a.art.startsWith("anruf") ? <PhoneIncoming className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
          {folgeLabel(a)}
        </Link>
      ))}
    </span>
  )
}

export function OffenerAnrufHinweis({ anruf, emailId, variante = "mail" }: {
  anruf: OffenerAnruf | null | undefined
  emailId?: number | string
  /** `mail`: Kasten an der Mail. `anruf`: Kasten am neueren Anruf desselben Kontakts. */
  variante?: "mail" | "anruf"
}) {
  const qc = useQueryClient()
  const sipgate = anruf?.quelle === "sipgate"
  const abhaken = useMutation({
    mutationFn: async () => {
      // Derselbe Haken wie in der jeweiligen Liste — Sipgate kennt „done",
      // der Voice-Agent „erledigt". Zwei Endpunkte, ein Knopf.
      const r = sipgate
        ? await fetch(`/api/v1/sipgate/calls/${encodeURIComponent(anruf!.call_id)}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "done" }),
          })
        : await fetch(`/admin/ki-telefon/api/call/${encodeURIComponent(anruf!.call_id)}/erledigt`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ erledigt: true }),
          })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json().catch(() => ({})) as Promise<{ schon?: boolean; hinweis?: string }>
    },
    onSuccess: (d) => {
      // #90: schon von jemand anderem abgehakt — sagen, von wem.
      if (d?.schon && d.hinweis) toast(`Anruf ${d.hinweis}`, "warn")
      else toast("Anruf als erledigt markiert", "success")
      if (emailId !== undefined) qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["ki-telefon-data"] })
      qc.invalidateQueries({ queryKey: ["sipgate-calls"] })
    },
    onError: (e: Error) => toast(`Abhaken fehlgeschlagen: ${e.message}`, "error"),
  })
  if (!anruf) return null
  const ziel = sipgate
    ? `/sipgate/${encodeURIComponent(anruf.call_id)}`
    : anruf.caller_number ? `/ki-telefon?caller=${encodeURIComponent(anruf.caller_number)}` : "/ki-telefon"
  return (
    <div
      className="mt-3 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-[12.5px] flex flex-wrap items-center gap-x-3 gap-y-1"
      data-testid="offener-anruf-hinweis"
    >
      <span className="inline-flex items-center gap-1.5 font-semibold">
        <PhoneIncoming className="w-3.5 h-3.5" />
        {variante === "anruf" ? "Älteres Telefonat vom " : "Rückmeldung zu offenem Telefonat vom "}{fmtTs(anruf.started_at)}
        {sipgate ? (anruf.kanal ? ` (${anruf.kanal})` : " (Sipgate)") : " (KI Telefon)"}
        {variante === "anruf" ? " noch nicht abgehakt" : ""}
      </span>
      {anruf.ai_headline && <span className="text-muted-foreground">{anruf.ai_headline}</span>}
      {anruf.zugewiesen_an && <span className="text-muted-foreground">zugeteilt: {anruf.zugewiesen_an}</span>}
      <span className="ml-auto inline-flex items-center gap-2">
        <Link to={ziel} className="underline text-primary">Anruf öffnen</Link>
        <button
          type="button"
          onClick={() => abhaken.mutate()}
          disabled={abhaken.isPending}
          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-[11.5px] font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Check className="w-3 h-3" /> Anruf abhaken
        </button>
      </span>
    </div>
  )
}
